import {keepPreviousData, useInfiniteQuery} from '@tanstack/react-query';

import {useSearchState} from '../../context/SearchContext';
import apiService from '../../transport/apiService';

import {buildSearchQueryKey, flattenFacets, searchKeys} from './keys';

interface UseSearchLinesOptions {
  /**
   * When true, previous query data is shown while a new query fetches.
   * Useful for RangeProductListScreen (smooth sort/filter transitions).
   * Disable for SearchScreen where rapid keyword changes cause stale
   * data races between isFetching and isPlaceholderData timing.
   */
  keepPrevious?: boolean;
}

export function useSearchLines({keepPrevious = true}: UseSearchLinesOptions = {}) {
  const state = useSearchState();
  const queryKey = buildSearchQueryKey(state);

  return useInfiniteQuery({
    queryKey: searchKeys.lines(state.storeId, queryKey),
    queryFn: async ({pageParam = 1}) => {
      const response = await apiService.getDepartmentLines({
        keywords: state.query || undefined,
        department_id: state.departmentId ?? undefined,
        sort_option_id: state.sortOptionId as Parameters<
          typeof apiService.getDepartmentLines
        >[0]['sort_option_id'],
        facets: flattenFacets(state.activeFacets),
        specials: state.specials || undefined,
        page: pageParam,
        page_size: 48,
        // Price range — previously omitted despite being in the query key,
        // causing the price filter UI to silently do nothing.
        sell_price_cents_minimum: state.priceRange ? state.priceRange.min * 100 : undefined,
        sell_price_cents_maximum: state.priceRange ? state.priceRange.max * 100 : undefined,
      });
      return response;
    },
    initialPageParam: 1,
    getNextPageParam: lastPage => {
      if (!lastPage?.meta) return undefined;
      const {page, page_count} = lastPage.meta;
      return page < page_count ? page + 1 : undefined;
    },
    // storeId gate prevents queries firing before store selection.
    // placeholderData is safe across store switches because SET_STORE_ID
    // resets query/departmentId atomically, disabling the query before
    // the key changes — so no cross-store placeholder flash occurs.
    enabled: !!state.storeId && (!!state.departmentId || state.specials || !!state.query),
    placeholderData: keepPrevious ? keepPreviousData : undefined,
  });
}
